import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { Sparkles, Upload, Download, ArrowLeft, X } from "lucide-react";

export default function Generate() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const inputsRef = useRef({}); // field_key -> input ref
  const [tpl, setTpl] = useState(null);
  const [data, setData] = useState({});
  const [photos, setPhotos] = useState({}); // {field_key: {path, preview}}
  const [uploading, setUploading] = useState({}); // {field_key: bool}
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: t } = await api.get(`/templates/${templateId}`);
        setTpl(t);
        const init = {};
        (t.fields || []).forEach(f => { if (f.type === "text") init[f.key] = ""; });
        setData(init);
      } catch (e) { console.error(e); toast.error("Template not found"); navigate("/templates"); }
    })();
  }, [templateId, navigate]);

  const uploadPhoto = async (fieldKey, file) => {
    if (!file) return;
    setUploading(u => ({ ...u, [fieldKey]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: r } = await api.post("/uploads/image", fd);
      setPhotos(p => ({ ...p, [fieldKey]: { path: r.path, preview: fileUrl(r.path) } }));
      toast.success(`Uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(u => ({ ...u, [fieldKey]: false })); }
  };

  const removePhoto = (fieldKey) => {
    setPhotos(p => { const c = { ...p }; delete c[fieldKey]; return c; });
  };

  const generate = async () => {
    setBusy(true); setResult(null);
    try {
      const photosPayload = {};
      Object.entries(photos).forEach(([k, v]) => { if (v?.path) photosPayload[k] = v.path; });
      const { data: card } = await api.post("/idcards/generate", {
        template_id: templateId,
        data,
        photos: photosPayload,
        person_name: data.name || data.Name || "Card",
      });
      setResult(card);
      toast.success("ID Card generated!");
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error("No credits left. Please buy credits.");
        setTimeout(()=>navigate("/billing"), 1200);
      } else {
        toast.error(err.response?.data?.detail || "Generation failed");
      }
    } finally { setBusy(false); }
  };

  const download = async (format) => {
    try {
      const res = await api.get(`/idcards/${result.id}/download?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${result.person_name}.${format}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (!tpl) return <div className="font-mono text-sm uppercase tracking-widest">Loading...</div>;

  const textFields = (tpl.fields || []).filter(f => f.type === "text");
  const photoFields = (tpl.fields || []).filter(f => f.type === "photo");

  return (
    <div className="space-y-6" data-testid="generate-page">
      <div className="flex items-center gap-3">
        <Link to="/templates" className="p-2 brutal-border brutal-shadow-sm bg-white hover:bg-gray-100"><ArrowLeft className="w-4 h-4"/></Link>
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1">// Single generation</div>
          <h1 className="font-display font-black text-3xl tracking-tighter">Generate: {tpl.name}</h1>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="bg-white brutal-border brutal-shadow p-6 space-y-4">
          <div className="font-mono text-xs uppercase tracking-widest text-gray-500">// Fill card data</div>
          {textFields.length === 0 && photoFields.length === 0 && (
            <div className="text-sm text-gray-500 font-mono">This template has no fields.</div>
          )}
          {textFields.map(f => (
            <div key={f.id}>
              <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">{f.label}</label>
              <input value={data[f.key] || ""} onChange={e=>setData(d=>({...d, [f.key]: e.target.value}))} className="brutal-input w-full" data-testid={`input-${f.key}`}/>
            </div>
          ))}

          {photoFields.length > 0 && (
            <div className="border-t-2 border-black pt-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3">// Images ({photoFields.length})</div>
              <div className="space-y-3">
                {photoFields.map(f => {
                  const p = photos[f.key];
                  const isBusy = uploading[f.key];
                  return (
                    <div key={f.id} className="brutal-border p-3 bg-gray-50" data-testid={`photo-field-${f.key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-bold text-sm">{f.label}</div>
                          <div className="font-mono text-[10px] text-gray-500 uppercase">key: {f.key} · {Math.round(f.width)}×{Math.round(f.height)}</div>
                        </div>
                        {p && (
                          <button onClick={()=>removePhoto(f.key)} className="p-1 text-[#FF3B30] hover:bg-red-50" data-testid={`remove-photo-${f.key}`}><X className="w-4 h-4"/></button>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-20 h-20 border-2 border-black bg-white flex items-center justify-center overflow-hidden shrink-0">
                          {p?.preview ? (
                            <img src={p.preview} alt="preview" className="w-full h-full object-cover"/>
                          ) : (
                            <span className="font-mono text-[10px] uppercase text-gray-400">Empty</span>
                          )}
                        </div>
                        <button
                          onClick={()=>inputsRef.current[f.key]?.click()}
                          disabled={isBusy}
                          className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-black font-bold uppercase text-xs tracking-widest px-4 py-3 brutal-border brutal-shadow-sm brutal-hover disabled:opacity-60"
                          data-testid={`upload-photo-${f.key}`}
                        >
                          <Upload className="w-4 h-4"/> {isBusy ? "..." : p ? "Change" : "Upload"}
                        </button>
                        <input
                          ref={el => (inputsRef.current[f.key] = el)}
                          type="file"
                          accept="image/*"
                          onChange={e => { uploadPhoto(f.key, e.target.files?.[0]); e.target.value = ""; }}
                          className="hidden"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <button onClick={generate} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#002FA7] text-white font-bold uppercase tracking-widest text-sm px-5 py-4 brutal-border brutal-shadow brutal-hover disabled:opacity-60" data-testid="generate-btn">
            <Sparkles className="w-4 h-4"/> {busy ? "Generating..." : "Generate HD Card"}
          </button>
        </div>

        {/* Result */}
        <div className="bg-white brutal-border brutal-shadow p-6">
          <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mb-3">// Preview / Result</div>
          {!result ? (
            <div className="aspect-[16/10] bg-[#E5E7EB] pattern-checkerboard flex items-center justify-center border-2 border-dashed border-black">
              <div className="font-mono text-xs uppercase tracking-widest text-gray-500">Awaiting generation</div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="result-block">
              <div className="relative">
                <img src={fileUrl(result.png_path)} alt="generated" className="w-full brutal-border"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={()=>download("png")} className="inline-flex items-center justify-center gap-2 bg-black text-white font-bold uppercase text-xs tracking-widest px-4 py-3 brutal-border brutal-shadow brutal-hover" data-testid="dl-png">
                  <Download className="w-4 h-4"/> PNG
                </button>
                <button onClick={()=>download("pdf")} className="inline-flex items-center justify-center gap-2 bg-[#002FA7] text-white font-bold uppercase text-xs tracking-widest px-4 py-3 brutal-border brutal-shadow brutal-hover" data-testid="dl-pdf">
                  <Download className="w-4 h-4"/> PDF
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
